import type { JSX } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { SchemaChangeImpact } from '@/types/machine-types';

/**
 * Plan 10-02 D-95 — SchemaChangeWarningDialog
 *
 * Surfaces when PUT /api/machine-types/:id returns 400 with `requiresForce=true`
 * plus the four impact arrays. The dialog displays:
 *   - Removed keys (machines lose this field on next save)
 *   - Changed data types (existing values may not coerce cleanly)
 *   - Added required fields (machines without this field become invalid)
 *   - Affected machine count (badge)
 *
 * Phase 10 specific note: `affectedMachineCount` is always 0 because the
 * Machine table doesn't exist yet. The dialog still renders the count for UX
 * consistency — Phase 11 will start producing non-zero counts and the same
 * dialog handles both.
 *
 * The dialog itself does NOT fire the API. The parent (MachineTypeEditPage)
 * passes `onConfirm` which retries `updateMachineTypeRequest(id, body, true)`.
 */
export interface SchemaChangeWarningDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly impact: SchemaChangeImpact | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly submitting?: boolean;
}

export function SchemaChangeWarningDialog({
  open,
  onOpenChange,
  impact,
  onConfirm,
  onCancel,
  submitting = false,
}: SchemaChangeWarningDialogProps): JSX.Element | null {
  if (impact === null) return null;

  const removed = impact.removedKeys;
  const changed = impact.changedDataTypes;
  const added = impact.addedRequired;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="schema-change-warning-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Schema change requires confirmation</AlertDialogTitle>
          <AlertDialogDescription>
            Your edits change the schema of this machine type in ways that affect existing machines.
            Review the impact below before continuing.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <Alert variant="destructive">
          <AlertTitle>Impact</AlertTitle>
          <AlertDescription>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm">Affected machines:</span>
              <Badge variant="secondary" data-testid="scw-affected-count">
                {String(impact.affectedMachineCount)}
              </Badge>
            </div>

            {removed.length > 0 && (
              <div className="mt-3" data-testid="scw-removed-keys">
                <div className="text-xs font-semibold uppercase">Removed keys</div>
                <ul className="list-disc pl-5 text-sm">
                  {removed.map((k) => (
                    <li key={k}>{k}</li>
                  ))}
                </ul>
              </div>
            )}

            {changed.length > 0 && (
              <div className="mt-3" data-testid="scw-changed-types">
                <div className="text-xs font-semibold uppercase">Changed data types</div>
                <ul className="list-disc pl-5 text-sm">
                  {changed.map((k) => (
                    <li key={k}>{k}</li>
                  ))}
                </ul>
              </div>
            )}

            {added.length > 0 && (
              <div className="mt-3" data-testid="scw-added-required">
                <div className="text-xs font-semibold uppercase">Added required fields</div>
                <ul className="list-disc pl-5 text-sm">
                  {added.map((k) => (
                    <li key={k}>{k}</li>
                  ))}
                </ul>
              </div>
            )}
          </AlertDescription>
        </Alert>

        <AlertDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onCancel();
            }}
            disabled={submitting}
            data-testid="scw-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              onConfirm();
            }}
            disabled={submitting}
            data-testid="scw-override"
          >
            {submitting ? 'Saving…' : 'Force Save'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
